import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CameraFeed } from './components/CameraFeed';
import { EmotionCard, EnergyCard, AttentionCard, FocusCard } from './components/LiveMetrics';
import { InsightsDashboard } from './components/InsightsDashboard';
import { SettingsPanel } from './components/SettingsPanel';
import { LandingPage } from './components/LandingPage';
import { LiveSuggestions } from './components/LiveSuggestions';
import { ShareCard } from './components/ShareCard';
import { useDetection } from './hooks/useDetection';
import { useSession } from './hooks/useSession';
import { useSettings } from './hooks/useStorage';
import { auth, loginWithGoogle, loginWithEmail, signupWithEmail, logout } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

const CSS_VARS = `
  :root {
    --bg:#f7faf9; --bg-glass:rgba(247,250,249,0.96);
    --surface:#ffffff;
    --surface2:#f0f5f4;
    --border:#d6e8e4;
    --accent:#0cb8a0;
    --accent2:#0a9688;
    --accent3:#06b6a2;
    --text:#111827;
    --muted:#6b7280;
    --energetic:#0cb8a0;
    --alert:#3b82f6;
    --tired:#f59e0b;
    --sleepy:#8b5cf6;
    --angry:#ef4444;
  }
  @keyframes spin   { to { transform:rotate(360deg) } }
  @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
  * { box-sizing:border-box; margin:0; padding:0 }
  body { background:var(--bg); color:var(--text); font-family:'Nunito',sans-serif }
  ::-webkit-scrollbar { width:4px }
  ::-webkit-scrollbar-track { background:transparent }
  ::-webkit-scrollbar-thumb { background:var(--border); border-radius:2px }
`;

const Btn = ({ children, onClick, disabled, variant = 'outline', style = {} }) => {
  const base = {
    padding: '9px 18px', borderRadius: 8, fontFamily: "'Nunito',sans-serif",
    fontSize: '0.72rem', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', ...style,
  };
  if (variant === 'primary') return <button onClick={onClick} disabled={disabled} style={{ ...base, border: 'none', background: 'var(--accent)', color: '#0a0a0f', fontWeight: 600 }}>{children}</button>;
  if (variant === 'danger')  return <button onClick={onClick} disabled={disabled} style={{ ...base, border: '1px solid var(--angry)', background: 'transparent', color: 'var(--angry)' }}>{children}</button>;
  return <button onClick={onClick} disabled={disabled} style={{ ...base, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)' }}>{children}</button>;
};

export default function App() {
  const [user,         setUser]         = useState(undefined);
  const [authLoading,  setAuthLoading]  = useState(false);
  const [authError,    setAuthError]    = useState('');
  const [mpReady,      setMpReady]      = useState(false);
  const [mpLoading,    setMpLoading]    = useState(false);
  const [mpError,      setMpError]      = useState('');
  const [tracking,     setTracking]     = useState(false);
  const [view,         setView]         = useState('live');
  const [showSettings, setShowSettings] = useState(false);
  const [showMesh,     setShowMesh]     = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('moodscope_dark_mode') === 'true'; } catch(e) { return false; }
  });
  const [trackingSecs, setTrackingSecs] = useState(0);
  const [cameraError,  setCameraError]  = useState(null);
  const [showShare,    setShowShare]    = useState(false);
  // Snapshots state — seeded from localStorage so Insights work immediately after refresh
  const [snapshots, setSnapshots] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('moodscope_snapshots_v1') || '[]');
      const today = new Date().toDateString();
      return saved.filter(s => new Date(s.timestamp).toDateString() === today);
    } catch(e) { return []; }
  });

  const videoRef   = useRef(null);
  const overlayRef = useRef(null);
  const streamRef  = useRef(null);

  const { settings, saveSettings } = useSettings();
  const {
    sessionId, snapshotCount,
    startSession, handleSnapshot, finishSession, getSnapshots, clearLocal,
  } = useSession(user);

  const {
    init, startLoop, stopLoop, reset, setMeshVisible,
    facePresent, emotion, expressions, energy, attentionScore,
    ear, blinkCount, blinkRate, blinkDurationAvg, perclos,
    gaze, headPose, focusState, focusPct, focusedMs, distractedMs,
    calibrated, calibProgress, yawnCount,
  } = useDetection({
    onSnapshot: handleSnapshot,
    onLiveMetrics: null,
    monitorSetup: settings.monitorSetup || 'single',
  });

  // Sync snapshots state from ref whenever snapshotCount changes
  useEffect(() => { setSnapshots(getSnapshots()); }, [snapshotCount, getSnapshots]);

  // Count seconds while tracking so we can show "insights in Xs" countdown
  useEffect(() => {
    if (!tracking) { setTrackingSecs(0); return; }
    const t = setInterval(() => setTrackingSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [tracking]);

  // Auto-save daily summary when tab is closed/refreshed without clicking Stop
  useEffect(() => {
    const handleUnload = () => {
      if (!tracking || !user) return;
      // saveDailySummary is async but beforeunload must be sync
      // Use sendBeacon-style approach via finishSession synchronously
      // We trigger finishSession which saves to Firebase
      finishSession();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [tracking, user, finishSession]);

  const handleToggleMesh = useCallback(() => {
    setShowMesh(prev => {
      const next = !prev;
      setMeshVisible(next);
      return next;
    });
  }, [setMeshVisible]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u || null));
    return unsub;
  }, []);

  const handleGoogleLogin = async () => {
    setAuthLoading(true); setAuthError('');
    try { await loginWithGoogle(); }
    catch (e) { setAuthError(e.code === 'auth/popup-closed-by-user' ? 'Sign-in cancelled.' : 'Google sign-in failed.'); }
    setAuthLoading(false);
  };

  const handleEmailLogin = async ({ email, password, name, isNew }) => {
    setAuthLoading(true); setAuthError('');
    try {
      if (isNew) await signupWithEmail(email, password, name);
      else await loginWithEmail(email, password);
    } catch (e) {
      const msgs = {
        'auth/user-not-found': 'No account with that email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/email-already-in-use': 'Account already exists.',
        'auth/weak-password': 'Password must be 6+ characters.',
        'auth/invalid-email': 'Invalid email.',
        'auth/invalid-credential': 'Incorrect email or password.',
      };
      setAuthError(msgs[e.code] || 'Sign-in failed.');
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await stopTracking();
    await logout();
  };

  useEffect(() => {
    if (!user || mpReady || mpLoading) return;
    setMpLoading(true);
    init().then(ok => {
      if (ok) setMpReady(true);
      else setMpError('MediaPipe failed to load. Check your connection and refresh.');
      setMpLoading(false);
    });
  }, [user, init, mpReady, mpLoading]);

  const startTracking = useCallback(async () => {
    if (!mpReady) return;
    // Request notification permission — fire and forget with timeout so it never blocks tracking
    if ('Notification' in window && Notification.permission === 'default') {
      Promise.race([
        Notification.requestPermission(),
        new Promise(r => setTimeout(r, 1000)),
      ]).catch(() => {});
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await new Promise(r => { videoRef.current.onloadedmetadata = r; });
      overlayRef.current.width  = videoRef.current.videoWidth  || 1280;
      overlayRef.current.height = videoRef.current.videoHeight || 720;
      await videoRef.current.play();
      setTracking(true);
      startLoop(videoRef.current, overlayRef.current);
      await startSession();
    } catch (e) {
      const msg = e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError'
        ? 'camera_denied'
        : e.name === 'NotFoundError'
        ? 'camera_not_found'
        : 'camera_error';
      setCameraError(msg);
    }
  }, [mpReady, startLoop, startSession]);

  const stopTracking = useCallback(async () => {
    setTracking(false);
    stopLoop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (overlayRef.current) {
      overlayRef.current.getContext('2d').clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    }
    await finishSession();
  }, [stopLoop, finishSession]);

  const handleClear = async () => {
    if (!window.confirm('Stop tracking and clear this session?')) return;
    await stopTracking();
    clearLocal();
    reset();
  };

  // Keyboard shortcut — Space to start/stop (placed after startTracking/stopTracking to avoid TDZ)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.code !== 'Space') return;
      if (['INPUT','TEXTAREA','BUTTON'].includes(e.target.tagName)) return;
      e.preventDefault();
      if (tracking) stopTracking();
      else if (mpReady && view === 'live') startTracking();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [tracking, mpReady, view, startTracking, stopTracking]);

  // Early returns
  if (user === undefined) {
    return (
      <>
        <style>{CSS_VARS + (darkMode ? `
  :root {
    --bg:#0d1117; --bg-glass:rgba(13,17,23,0.96); --surface:#161b22; --surface2:#21262d;
    --border:#30363d; --accent:#0cb8a0; --accent2:#0a9688;
    --text:#e6edf3; --muted:#8b949e;
    --energetic:#0cb8a0; --alert:#58a6ff; --tired:#d29922;
    --sleepy:#8b5cf6; --angry:#f85149;
  }
` : '')}</style>
        <div style={{ background: 'var(--bg)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20 }}>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: '2rem' }}>Mood<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>scope</span></div>
          <div style={{ width: 30, height: 30, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <style>{CSS_VARS + (darkMode ? `
  :root {
    --bg:#0d1117; --bg-glass:rgba(13,17,23,0.96); --surface:#161b22; --surface2:#21262d;
    --border:#30363d; --accent:#0cb8a0; --accent2:#0a9688;
    --text:#e6edf3; --muted:#8b949e;
    --energetic:#0cb8a0; --alert:#58a6ff; --tired:#d29922;
    --sleepy:#8b5cf6; --angry:#f85149;
  }
` : '')}</style>
        <LandingPage onGoogleLogin={handleGoogleLogin} onEmailLogin={handleEmailLogin} loading={authLoading} error={authError} />
      </>
    );
  }

  const userInitial = (user.displayName || user.email || '?')[0].toUpperCase();
  const liveMetrics = { attentionScore, emotion, energy, blinkRate, blinkDurationAvg, perclos, yawnCount, focusPct };

  return (
    <>
      <style>{CSS_VARS + (darkMode ? `
  :root {
    --bg:#0d1117; --bg-glass:rgba(13,17,23,0.96); --surface:#161b22; --surface2:#21262d;
    --border:#30363d; --accent:#0cb8a0; --accent2:#0a9688;
    --text:#e6edf3; --muted:#8b949e;
    --energetic:#0cb8a0; --alert:#58a6ff; --tired:#d29922;
    --sleepy:#8b5cf6; --angry:#f85149;
  }
` : '')}</style>
      <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh', transition: 'background 0.3s, color 0.3s' }}>

        {(mpLoading || !mpReady) && !mpError && (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, zIndex: 200 }}>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: '2rem' }}>Mood<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>scope</span></div>
            <div style={{ width: 34, height: 34, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.76rem', color: 'var(--muted)', letterSpacing: '0.1em' }}>Loading MediaPipe Face Mesh…</span>
            <span style={{ fontSize: '0.65rem', color: 'var(--muted)', opacity: 0.5 }}>First load may take a few seconds</span>
          </div>
        )}

        {mpError && (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, zIndex: 200, padding: 30 }}>
            <div style={{ fontSize: '2rem' }}>⚠</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--angry)', textAlign: 'center', maxWidth: 380 }}>{mpError}</div>
            <Btn variant="primary" onClick={() => window.location.reload()}>Reload</Btn>
          </div>
        )}

        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px clamp(14px,4vw,36px)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-glass)', boxShadow: '0 1px 8px rgba(0,0,0,0.08)', backdropFilter: 'blur(12px)', zIndex: 100, gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 'clamp(1rem,3vw,1.4rem)' }}>
            Mood<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>scope</span>
          </div>
          <div style={{ display: 'flex', background: 'var(--surface2)', borderRadius: 8, padding: 3, gap: 2 }}>
            {[['live', '📡 Live'], ['insights', '📊 Insights']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: view === v ? 'var(--surface)' : 'transparent', color: view === v ? 'var(--text)' : 'var(--muted)', fontFamily: "'Nunito',sans-serif", fontSize: '0.68rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                {label}{v === 'insights' && snapshots.length > 0 ? ` (${snapshots.length})` : ''}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {snapshotCount > 0 && (
              <div style={{ fontSize: '0.6rem', color: 'var(--accent2)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 8px' }}>
                ☁ {snapshotCount} snapshots
              </div>
            )}
            {snapshots.length > 0 && (
              <button onClick={() => setShowShare(true)} title="Share your session" style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: '0.65rem', cursor: 'pointer', fontFamily:"'Nunito',sans-serif", display:'flex', alignItems:'center', gap:4 }}>
                ↑ Share
              </button>
            )}
            <button onClick={() => setDarkMode(d => {
              const next = !d;
              try { localStorage.setItem('moodscope_dark_mode', String(next)); } catch(e) {}
              return next;
            })} title={darkMode ? 'Light mode' : 'Dark mode'} style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: darkMode ? 'var(--surface2)' : 'transparent', color: 'var(--text)', fontSize: '0.85rem', cursor: 'pointer', lineHeight: 1 }}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={() => setShowSettings(true)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontFamily: "'Nunito',sans-serif", fontSize: '0.67rem', cursor: 'pointer' }}>
              ⚙ {settings.monitorSetup}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'var(--accent)' }}>
                {userInitial}
              </div>
              <button onClick={handleLogout} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: '0.63rem', cursor: 'pointer', textDecoration: 'underline' }}>Sign out</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: tracking ? 'var(--accent)' : 'var(--muted)', boxShadow: tracking ? '0 0 12px var(--accent)' : 'none', animation: tracking ? 'pulse 2s infinite' : 'none' }} />
              <span style={{ color: 'var(--muted)', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{tracking ? 'Live' : 'Idle'}</span>
            </div>
          </div>
        </header>

        {/* Share card modal */}
        {showShare && (
          <ShareCard
            snapshots={snapshots}
            onClose={() => setShowShare(false)}
          />
        )}

        {/* Live view — always mounted to preserve video stream */}
        <div style={{ display: view === 'live' ? 'grid' : 'none', gridTemplateColumns: 'minmax(0, 420px) 1fr', minHeight: 'calc(100vh - 57px)', alignItems: 'start' }}>
          <div style={{ padding: 'clamp(10px,2vw,18px) clamp(10px,2vw,18px)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 57, maxHeight: 'calc(100vh - 57px)', overflowY: 'auto' }}>
            <CameraFeed
              videoRef={videoRef} overlayRef={overlayRef}
              tracking={tracking} facePresent={facePresent}
              streamRef={streamRef} showMesh={showMesh} onToggleMesh={handleToggleMesh}
            />
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {[
                { text: `👁 EAR: ${ear ? ear.toFixed(3) : '—'}`,                              alert: false },
                { text: `😉 Blinks: ${blinkCount}`,                                             alert: false },
                { text: `⚡ ${blinkRate !== null ? blinkRate.toFixed(1) : '—'}/min`,            alert: false },
                { text: `😴 PERCLOS: ${perclos !== null ? (perclos*100).toFixed(0) : '—'}%`,   alert: perclos > 0.10 },
                { text: `🥱 Yawns: ${yawnCount}`,                                              alert: yawnCount > 3 },
                { text: `💓 Blink: ${blinkDurationAvg || '—'}ms`,                              alert: blinkDurationAvg > 300 },
              ].map((p, i) => (
                <div key={i} style={{ background: 'var(--surface2)', border: `1px solid ${p.alert ? 'var(--sleepy)' : 'var(--border)'}`, borderRadius: 20, padding: '4px 11px', fontSize: '0.63rem', color: p.alert ? 'var(--sleepy)' : 'var(--muted)' }}>
                  {p.text}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!tracking
                ? <Btn variant="primary" onClick={startTracking} disabled={!mpReady}>▶ Start Tracking</Btn>
                : <Btn onClick={stopTracking}>⏸ Stop</Btn>
              }
              {snapshots.length === 0 && tracking ? (
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', fontSize:'0.7rem', color:'var(--muted)', fontFamily:"'Nunito',sans-serif" }}>
                  📊 Insights in {Math.max(0, 60 - trackingSecs)}s
                </div>
              ) : (
                <Btn onClick={() => setView('insights')} disabled={snapshots.length === 0}>📊 Insights</Btn>
              )}
              <Btn variant="danger" onClick={handleClear} disabled={!tracking && snapshots.length === 0}>✕ Clear</Btn>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'stretch' }}>
              <EmotionCard emotion={emotion} expressions={expressions} />
              <EnergyCard energy={energy} ear={ear} blinkRate={blinkRate} blinkCount={blinkCount} blinkDurationAvg={blinkDurationAvg} calibrated={calibrated} calibProgress={calibProgress} />
            </div>
          </div>
          <div style={{ padding: 'clamp(10px,2vw,20px) clamp(12px,2vw,22px)', overflowY: 'auto', maxHeight: 'calc(100vh - 57px)', display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 57 }}>
            <AttentionCard attentionScore={attentionScore} perclos={perclos} gazeOnScreen={gaze ? !(Math.abs(headPose?.yaw || 0) > 0.30) : null} headPose={headPose} yawnCount={yawnCount} />
            <FocusCard focusState={focusState} focusPct={focusPct} focusedMs={focusedMs} distractedMs={distractedMs} />
            <LiveSuggestions metrics={liveMetrics} tracking={tracking} sessionMinutes={snapshotCount} user={user} />
            {sessionId && (
              <div style={{ fontSize: '0.58rem', color: 'var(--muted)', textAlign: 'center', opacity: 0.5 }}>
                Session {sessionId.slice(0, 8)}… · {snapshotCount} snapshots saved locally
              </div>
            )}
          </div>
        </div>

        {/* Insights view */}
        {view === 'insights' && (
          <div style={{ maxWidth: '100%', width: '100%', padding: 'clamp(12px,3vw,28px) clamp(16px,5vw,60px)', animation: 'fadeIn 0.3s ease', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.4rem' }}>Today's Insights</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--muted)', marginTop: 4 }}>
                  {snapshots.length} minute{snapshots.length !== 1 ? 's' : ''} of data · {new Date().toLocaleDateString()}
                </div>
              </div>
              <Btn onClick={() => setView('live')}>← Live</Btn>
            </div>
            <InsightsDashboard snapshots={snapshots} user={user} />
          </div>
        )}

      </div>

      {showSettings && (
        <SettingsPanel settings={settings} onSave={saveSettings} onClose={() => setShowSettings(false)} />
      )}
    </>
  );
}
